/**
 * Nodo TipTap "emailButton": bottone CTA (testo + link + colore) per il corpo newsletter.
 *
 * Vincoli di resa nei client email (Outlook/Gmail non applicano CSS esterno):
 *  - il bottone è un <a> con SOLI stili inline, avvolto in un <div> che ne gestisce l'allineamento;
 *  - niente border-radius "obbligatorio" per la resa: degrada a rettangolo dove non supportato.
 *
 * Il backend non sanitizza body_html (NewsletterRenderer lo passa as-is), quindi l'HTML prodotto
 * qui arriva intatto nell'email; l'href viene riscritto dal link-rewrite del tracking come ogni
 * altro link, perché è serializzato nella forma href="..." attesa da NewsletterRenderer.
 *
 * Gli attributi sono duplicati come data-* sul wrapper per poter essere riletti al reload
 * (parseHTML) senza dover interpretare la stringa di stile.
 */
// Importati da @tiptap/react (che ri-esporta @tiptap/core) per non dipendere da un pacchetto
// non dichiarato fra le dependencies del progetto.
import { Node, mergeAttributes } from '@tiptap/react';

export const DEFAULT_BUTTON_COLOR = '#1976d2';

/** Palette rapida proposta nel dialog. */
export const BUTTON_COLOR_PRESETS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#d32f2f',
  '#5e35b1',
  '#0288d1',
  '#212b36',
];

/**
 * Colore testo leggibile sul colore di sfondo scelto (luminanza relativa, soglia WCAG-like).
 * Evita di chiedere all'utente un secondo colore e previene bottoni illeggibili.
 */
export function readableTextColor(bgColor) {
  const hex = String(bgColor || '').replace('#', '');
  if (hex.length !== 6) return '#ffffff';

  const channel = (start) => {
    const c = parseInt(hex.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

  return luminance > 0.5 ? '#212b36' : '#ffffff';
}

/** Normalizza l'URL: aggiunge https:// se manca lo schema (coerente con setLink della toolbar). */
export function normalizeButtonHref(url) {
  const trimmed = String(url || '').trim();
  if (trimmed === '') return '';
  // I placeholder dinamici ({{unsubscribe_url}}) vanno lasciati intatti.
  if (trimmed.startsWith('{{')) return trimmed;
  return /^(https?:\/\/|mailto:|tel:)/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const buttonStyle = (bgColor) =>
  [
    'display:inline-block',
    `background-color:${bgColor}`,
    `color:${readableTextColor(bgColor)}`,
    'padding:12px 24px',
    'border-radius:6px',
    'text-decoration:none',
    'font-family:Arial,Helvetica,sans-serif',
    'font-size:15px',
    'font-weight:bold',
    'line-height:1.2',
    'mso-padding-alt:0',
  ].join(';');

/**
 * Il wrapper serve solo a dare al bottone una riga propria e i margini verticali: non impone
 * allineamento, così l'<a> (inline-block) occupa esattamente la larghezza del suo contenuto
 * e resta allineato al flusso del testo.
 */
const WRAPPER_STYLE = 'margin:16px 0';

export const EmailButton = Node.create({
  name: 'emailButton',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      label: { default: 'Scopri di più' },
      href: { default: '' },
      bgColor: { default: DEFAULT_BUTTON_COLOR },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-email-button]',
        getAttrs: (element) => {
          const anchor = element.querySelector('a');
          return {
            label: anchor?.textContent?.trim() || 'Scopri di più',
            href: anchor?.getAttribute('href') || '',
            bgColor: element.getAttribute('data-bg-color') || DEFAULT_BUTTON_COLOR,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { label, href, bgColor } = node.attrs;

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-email-button': '',
        'data-bg-color': bgColor,
        style: WRAPPER_STYLE,
      }),
      [
        'a',
        {
          href: normalizeButtonHref(href) || '#',
          target: '_blank',
          rel: 'noopener noreferrer',
          style: buttonStyle(bgColor),
        },
        label,
      ],
    ];
  },

  addCommands() {
    return {
      /** Inserisce un nuovo bottone alla posizione corrente. */
      setEmailButton:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),

      /** Aggiorna il bottone attualmente selezionato. */
      updateEmailButton:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, attrs),

      /** Elimina il bottone attualmente selezionato. */
      removeEmailButton:
        () =>
        ({ commands }) =>
          commands.deleteSelection(),
    };
  },
});

export default EmailButton;
